import React from 'react';
import { Protect } from '@clerk/clerk-react';

import { Page } from '@nearst/ui';
import { useShop } from '@services/ShopProvider';
import DatePicker from '@components/DatePicker';
import { useIsOnboarding } from '@components/OnboardingAccessControl';
import { RedirectWithShopId } from '@utils/RedirectWithShopId';
import AccessDenied from '@pages/errors/AccessDenied';

import { InventoryConnectionCard } from './cards/InventoryConnectionCard';
import { ChannelsCard } from './cards/ChannelsCard';
import { ReachCard } from './cards/ReachCard';
import { PerformanceCard } from './cards/PerformanceCard';
import { LocalAdsCard } from './cards/LocalAdsCard';
import { OrdersCard } from './cards/OrdersCard';

import styles from './DashboardOverview.module.scss';

/*
 * NEA-5635 retailer dashboard landing page. Replaces the legacy <Dashboard />
 * at `/dashboard/:shopId?`. Layout mirrors nearst/dashboard-refresh's
 * src/pages/Index.tsx but maps onto the existing dashboard tokens
 * (var(--white), var(--blue), etc.) and the shop/channel data hooks. See
 * docs/lovable-design-system.md for the token mapping.
 *
 * The design's bottom row of 4 Quick Action buttons (Pricing & Promotions,
 * PIM, Inventory Optimisation, In-store Inventory Feeds) is intentionally
 * NOT rendered — those destination pages don't exist in the dashboard yet.
 * Re-add when the underlying pages are built.
 */

const DashboardOverviewContent = () => {
	const { shop, organisation } = useShop();
	const pageTitle = shop?.id ? shop.name : (organisation?.name ?? 'Overview');

	return (
		<>
			<Page.Header>
				<h1>{pageTitle}</h1>
				<div className={styles.datePickerWrap}>
					<DatePicker />
				</div>
			</Page.Header>
			<Page.Section>
				<div className={styles.topRow}>
					<InventoryConnectionCard />
					<ReachCard />
					<PerformanceCard />
				</div>
				<div className={styles.journeyRow}>
					<ChannelsCard />
					<LocalAdsCard />
					<OrdersCard />
				</div>
			</Page.Section>
		</>
	);
};

// Mirrors the wrapping the legacy @pages/dashboard Dashboard component applied —
// onboarding redirect + permission gate — so retailers in setup or without
// insights access continue to see the right state at /dashboard.
export const DashboardOverview = () => {
	const isOnboarding = useIsOnboarding();
	if (isOnboarding) {
		return <RedirectWithShopId to={'/onboarding/:shopId'} />;
	}
	return (
		<Protect permission="org:insights:read" fallback={<AccessDenied />}>
			<DashboardOverviewContent />
		</Protect>
	);
};

export default DashboardOverview;
